import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Paragraph from '@tiptap/extension-paragraph'
import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { marked } from 'marked'
import styles from './StrategyDocAgent.module.css'

type PageState = 'idle' | 'loading' | 'streaming' | 'editing' | 'creating-doc' | 'doc-created'

interface StoredSelection {
  from: number
  to: number
  text: string
}

const DOC_ICON_PATH = 'M3 1 L3 15 L13 15 L13 4 L10 1 Z M10 1 L10 4 L13 4 M5 7 L11 7 M5 10 L11 10 M5 13 L9 13'

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      lineHeight: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).style.lineHeight || null,
        renderHTML: (attrs) => attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {},
      },
      paragraphSpacing: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).style.marginBottom || null,
        renderHTML: (attrs) => attrs.paragraphSpacing ? { style: `margin-bottom: ${attrs.paragraphSpacing}` } : {},
      },
    }
  },
})

export default function StrategyDocAgent() {
  const [files, setFiles] = useState<File[]>([])
  const [instructions, setInstructions] = useState('')
  const [pageState, setPageState] = useState<PageState>('idle')
  const [streamingText, setStreamingText] = useState('')
  const [docTitle, setDocTitle] = useState('Strategy Document')
  const [storedSelection, setStoredSelection] = useState<StoredSelection | null>(null)
  const [commentInput, setCommentInput] = useState('')
  const [editLoading, setEditLoading] = useState(false)
  const [createdDocUrl, setCreatedDocUrl] = useState<string | null>(null)
  const streamingPanelRef = useRef<HTMLDivElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const instructionsRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = streamingPanelRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [streamingText])

  const editor = useEditor({
    extensions: [StarterKit.configure({ paragraph: false }), CustomParagraph, TextStyle, Color],
    content: '',
    onSelectionUpdate({ editor: ed }) {
      const { from, to } = ed.state.selection
      if (from !== to) {
        const text = ed.state.doc.textBetween(from, to)
        setStoredSelection({ from, to, text })
      }
    },
  })

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    dropZoneRef.current?.classList.remove(styles['dropzone--over'])
    addFiles(Array.from(e.dataTransfer.files))
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    dropZoneRef.current?.classList.add(styles['dropzone--over'])
  }

  function handleDragLeave() {
    dropZoneRef.current?.classList.remove(styles['dropzone--over'])
  }

  function addFiles(newFiles: File[]) {
    const allowed = newFiles.filter((f) => /\.(pdf|docx|txt|md|png|jpg|jpeg)$/i.test(f.name))
    setFiles((prev) => [...prev, ...allowed])
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  function resizeInstructions() {
    const el = instructionsRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  async function generate() {
    if (!instructions.trim() || pageState === 'loading') return
    setPageState('loading')

    const formData = new FormData()
    formData.append('instructions', instructions)
    for (const file of files) {
      formData.append('files', file)
    }

    try {
      const res = await fetch('/api/strategy-doc', { method: 'POST', body: formData })
      if (!res.ok || !res.body) throw new Error('Generation failed')

      setPageState('streaming')
      setStreamingText('')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let accumulated = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6)
          if (payload === '[DONE]') continue
          try {
            const parsed = JSON.parse(payload) as { text: string }
            accumulated += parsed.text
            setStreamingText(accumulated)
          } catch {
            // ignore malformed lines
          }
        }
      }

      const html = await marked.parse(accumulated)
      editor?.commands.setContent(html)

      const titleMatch = accumulated.match(/^#\s+(.+)/m)
      if (titleMatch) setDocTitle(titleMatch[1].trim())

      setPageState('editing')
    } catch {
      setPageState('idle')
    }
  }

  async function submitComment() {
    if (!storedSelection || !commentInput.trim() || !editor || editLoading) return
    setEditLoading(true)

    const fullDocument = editor.getText()

    try {
      const res = await fetch('/api/strategy-doc-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedText: storedSelection.text,
          comment: commentInput,
          fullDocument,
        }),
      })
      const data = (await res.json()) as { replacement?: string }
      if (data.replacement) {
        editor
          .chain()
          .focus()
          .deleteRange({ from: storedSelection.from, to: storedSelection.to })
          .insertContentAt(storedSelection.from, data.replacement)
          .run()
        setStoredSelection(null)
        setCommentInput('')
      }
    } finally {
      setEditLoading(false)
    }
  }

  async function createGoogleDoc() {
    if (!editor || pageState === 'creating-doc') return
    setPageState('creating-doc')
    try {
      const res = await fetch('/api/create-google-doc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editor.getHTML(), title: docTitle }),
      })
      const data = (await res.json()) as { docUrl?: string; error?: string }
      if (data.docUrl) {
        setCreatedDocUrl(data.docUrl)
        setPageState('doc-created')
      } else {
        alert(`Failed to create Google Doc: ${data.error ?? 'Unknown error'}`)
        setPageState('editing')
      }
    } catch {
      setPageState('editing')
    }
  }

  return (
    <div className={styles['page']}>
      <div className={styles['page-header']}>
        <Link to='/' className={styles['back-link']}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Home
        </Link>
        <div className={styles['header-title']}>
          <span className={styles['tool-name']}>Strategy Doc Generator</span>
          <span className={styles['badge']}>Skill</span>
        </div>
      </div>

      <div className={styles['body']}>
        {/* Left panel */}
        <div className={styles['left-panel']}>
          <p className={styles['panel-label']}>Files</p>
          <div
            ref={dropZoneRef}
            className={styles['dropzone']}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <svg
              className={styles['dropzone-icon']}
              viewBox='0 0 16 16'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.25'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M8 1 L8 11 M4 5 L8 1 L12 5' />
              <path d='M2 11 L2 14 L14 14 L14 11' />
            </svg>
            <span className={styles['dropzone-text']}>Drag & drop or click to browse</span>
            <span className={styles['dropzone-hint']}>PDF, DOCX, TXT, MD, PNG, JPG</span>
          </div>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            accept='.pdf,.docx,.txt,.md,.png,.jpg,.jpeg'
            className={styles['file-input']}
            onChange={(e) => {
              if (e.target.files) addFiles(Array.from(e.target.files))
            }}
          />
          {files.length > 0 && (
            <div className={styles['file-chips']}>
              {files.map((f, i) => (
                <span key={i} className={styles['file-chip']}>
                  <span className={styles['file-chip-name']}>{f.name}</span>
                  <button className={styles['file-chip-remove']} onClick={() => removeFile(i)}>×</button>
                </span>
              ))}
            </div>
          )}

          <p className={styles['panel-label']}>Instructions</p>
          <textarea
            ref={instructionsRef}
            className={styles['instructions-textarea']}
            value={instructions}
            placeholder='Describe what you need in the strategy doc…'
            rows={4}
            onChange={(e) => {
              setInstructions(e.target.value)
              resizeInstructions()
            }}
          />
          <button
            className={styles['generate-button']}
            disabled={!instructions.trim() || pageState === 'loading' || pageState === 'streaming'}
            onClick={generate}
          >
            {pageState === 'loading' || pageState === 'streaming' ? 'Generating…' : 'Create Strategy Doc'}
          </button>
        </div>

        {/* Right panel */}
        <div className={styles['right-panel']}>
          {pageState === 'idle' && (
            <div className={styles['empty-state']}>
              <svg
                className={styles['empty-icon']}
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.25'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d={DOC_ICON_PATH} />
              </svg>
              <p className={styles['empty-headline']}>Provide me the inputs and I'll write your strategy doc.</p>
            </div>
          )}

          {pageState === 'loading' && (
            <div className={styles['empty-state']}>
              <div className={styles['spinner']} />
              <p className={styles['empty-headline']}>Henry is writing your strategy doc…</p>
            </div>
          )}

          {pageState === 'streaming' && (
            <div ref={streamingPanelRef} className={styles['streaming-panel']}>
              <div className={styles['streaming-text']}>{streamingText}</div>
            </div>
          )}

          {(pageState === 'editing' || pageState === 'creating-doc' || pageState === 'doc-created') && editor && (
            <>
              <BubbleMenu
                editor={editor}
                tippyOptions={{
                  duration: 100,
                  onHide: () => setCommentInput(''),
                }}
              >
                <div
                  className={styles['bubble-menu']}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    className={styles['bubble-input']}
                    value={commentInput}
                    placeholder='Edit instruction…'
                    onChange={(e) => setCommentInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void submitComment()
                    }}
                  />
                  <button
                    className={styles['bubble-submit']}
                    disabled={!commentInput.trim() || editLoading}
                    onClick={() => void submitComment()}
                  >
                    {editLoading ? '…' : 'Edit'}
                  </button>
                </div>
              </BubbleMenu>

              {pageState === 'doc-created' && createdDocUrl && (
                <div className={styles['doc-created-state']}>
                  <div className={styles['doc-created-check']}>
                    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M20 6 L9 17 L4 12' />
                    </svg>
                  </div>
                  <p className={styles['doc-created-headline']}>Your Google Doc is ready</p>
                  <a
                    href={createdDocUrl}
                    target='_blank'
                    rel='noreferrer'
                    className={styles['gdoc-file-button']}
                  >
                    <svg className={styles['gdoc-file-icon']} viewBox='0 0 40 52' fill='none'>
                      <path d='M0 4 C0 1.8 1.8 0 4 0 L28 0 L40 12 L40 48 C40 50.2 38.2 52 36 52 L4 52 C1.8 52 0 50.2 0 48 Z' fill='#4285F4' />
                      <path d='M28 0 L28 12 L40 12 Z' fill='#A8C7FA' />
                      <rect x='8' y='22' width='24' height='2.5' rx='1.25' fill='white' fillOpacity='0.9' />
                      <rect x='8' y='29' width='24' height='2.5' rx='1.25' fill='white' fillOpacity='0.9' />
                      <rect x='8' y='36' width='16' height='2.5' rx='1.25' fill='white' fillOpacity='0.9' />
                    </svg>
                    <div className={styles['gdoc-file-meta']}>
                      <span className={styles['gdoc-file-name']}>{docTitle}</span>
                      <span className={styles['gdoc-file-type']}>Google Docs · Click to open</span>
                    </div>
                    <svg className={styles['gdoc-file-arrow']} viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M3 8 L13 8 M9 4 L13 8 L9 12' />
                    </svg>
                  </a>
                </div>
              )}

              {pageState !== 'doc-created' && (
                <>
                  <div className={styles['toolbar']}>
                    <button
                      className={`${styles['toolbar-btn']} ${editor.isActive('bold') ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run() }}
                    >B</button>
                    <button
                      className={`${styles['toolbar-btn']} ${styles['toolbar-btn--italic']} ${editor.isActive('italic') ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run() }}
                    >I</button>
                    <span className={styles['toolbar-sep']} />
                    <button
                      className={`${styles['toolbar-btn']} ${editor.isActive('heading', { level: 1 }) ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 1 }).run() }}
                    >H1</button>
                    <button
                      className={`${styles['toolbar-btn']} ${editor.isActive('heading', { level: 2 }) ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run() }}
                    >H2</button>
                    <button
                      className={`${styles['toolbar-btn']} ${editor.isActive('heading', { level: 3 }) ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 3 }).run() }}
                    >H3</button>
                    <span className={styles['toolbar-sep']} />
                    <button
                      className={`${styles['toolbar-btn']} ${editor.isActive('bulletList') ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run() }}
                    >• —</button>
                    <button
                      className={`${styles['toolbar-btn']} ${editor.isActive('orderedList') ? styles['toolbar-btn--active'] : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run() }}
                    >1 —</button>
                    <span className={styles['toolbar-sep']} />
                    <div className={styles['toolbar-color-wrap']}>
                      <input
                        type='color'
                        className={styles['toolbar-color']}
                        title='Text colour'
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => { editor.chain().focus().setColor(e.target.value).run() }}
                      />
                      <button
                        className={styles['toolbar-btn']}
                        title='Remove colour'
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetColor().run() }}
                      >✕</button>
                    </div>
                    <span className={styles['toolbar-sep']} />
                    <select
                      className={styles['toolbar-select']}
                      title='Line height'
                      defaultValue=''
                      onChange={(e) => {
                        const val = e.target.value
                        editor.chain().focus().updateAttributes('paragraph', { lineHeight: val || null }).run()
                      }}
                    >
                      <option value='' disabled>Line</option>
                      <option value='1'>1.0</option>
                      <option value='1.15'>1.15</option>
                      <option value='1.5'>1.5</option>
                      <option value='1.7'>1.7</option>
                      <option value='2'>2.0</option>
                    </select>
                    <select
                      className={styles['toolbar-select']}
                      title='Paragraph spacing'
                      defaultValue=''
                      onChange={(e) => {
                        const val = e.target.value
                        editor.chain().focus().updateAttributes('paragraph', { paragraphSpacing: val || null }).run()
                      }}
                    >
                      <option value='' disabled>Spacing</option>
                      <option value='0px'>Compact</option>
                      <option value='12px'>Normal</option>
                      <option value='24px'>Relaxed</option>
                    </select>
                  </div>

                  <div className={styles['editor-scroll']}>
                    <EditorContent editor={editor} className={styles['editor-content']} />
                  </div>

                  <div className={styles['editor-footer']}>
                    <span className={styles['footer-hint']}>All Done?</span>
                    <button
                      className={styles['google-doc-button']}
                      onClick={() => void createGoogleDoc()}
                      disabled={pageState === 'creating-doc'}
                    >
                      {pageState === 'creating-doc' ? 'Creating…' : 'Create Google Doc →'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
